import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, Skeleton } from '../../design-system';
import { searchEverything, searchSources } from '../../lib/command';
import './CommandBar.css';

// One box that finds anything in the practice.
//
// ⚠️ THE TYPE CHIPS ARE BUILT FROM THE SERVER'S ANSWER, not from a hardcoded
// list. /api/ai/sources returns only the sources this caller's nav keys let
// them search, so somebody without `nav.finance` never sees an "Invoices" chip
// that would always come back empty — the filter tells the truth about what
// this person can find.
export default function SmartSearch() {
  const [q, setQ] = useState('');
  const [sources, setSources] = useState([]);
  const [active, setActive] = useState([]);   // [] = everything
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { searchSources().then(setSources).catch(() => {}); }, []);

  const run = useCallback(async (text, types) => {
    // Two characters matches most of the database and helps nobody; the server
    // enforces the same floor.
    if (text.trim().length < 2) { setData(null); return; }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      setData(await searchEverything({ q: text, types, signal: controller.signal }));
    } catch {
      // An aborted request is the normal case while typing — it is superseded,
      // not failed, and blanking the list on it would make results flicker.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => run(q, active), 220);
    return () => clearTimeout(t);
  }, [q, active, run]);

  const toggle = (key) => setActive((a) => (
    a.includes(key) ? a.filter((k) => k !== key) : [...a, key]
  ));

  return (
    <div className="cmd-search">
      <input
        className="cmd-text-input cmd-search-input"
        value={q}
        autoFocus
        placeholder="Search clients, tasks, notices, documents, invoices, people…"
        onChange={(e) => setQ(e.target.value)}
      />

      {sources.length > 1 && (
        <div className="cmd-search-types">
          <button
            type="button"
            className={`cmd-mode${active.length === 0 ? ' is-on' : ''}`}
            onClick={() => setActive([])}
          >
            Everything
          </button>
          {sources.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`cmd-mode${active.includes(s.key) ? ' is-on' : ''}`}
              onClick={() => toggle(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {loading && !data && <Skeleton.Text lines={4} />}

      {data && !data.groups.length && (
        <EmptyState
          title="Nothing found"
          description={`No match for “${data.query}” in what you have access to.`}
        />
      )}

      {/* ⚠️ A NEAR MISS IS SAID OUT LOUD, ONCE, ABOVE THE LOT. Somebody who
          mistyped a name and is handed a confident-looking list has no way to
          tell they are not looking at what they asked for — and the closer the
          guess, the more convincing the wrong answer. Rendered only when EVERY
          group is approximate, so a search that half-matched does not get a
          banner contradicting the rows that are exact. */}
      {data?.approximate && (
        <p className="cmd-search-approx">
          No exact match for <strong>“{data.query}”</strong>. Showing the closest
          names instead.
        </p>
      )}

      {data?.groups.map((g) => (
        <div key={g.key} className="cmd-search-group">
          <h4 className="cmd-search-group-title">
            {g.label}
            {/* The per-group note carries the mixed case the banner cannot:
                some sources matched exactly, this one only came close. */}
            {g.approximate && !data.approximate && (
              <span className="cmd-search-approx-tag">closest matches</span>
            )}
          </h4>
          <ul className="cmd-search-results">
            {g.results.map((r) => (
              <li key={`${r.type}-${r.id}`}>
                <button type="button" className="cmd-search-hit" onClick={() => navigate(r.path)}>
                  <span className="cmd-search-hit-title">{r.title}</span>
                  {r.subtitle && <span className="cmd-search-hit-sub">{r.subtitle}</span>}
                  {r.badge && <span className="cmd-search-hit-badge">{r.badge}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
